import { useRouter } from 'next/router';
import useIntl from 'react-intl/src/components/useIntl';

import { isNotEmpty } from 'src/common/utils/arrayUtils';
import { StringCaseType, StringUtils } from 'src/common/utils/stringUtils';

import { MetaDataLocaleKey } from '../Components/Pages/InternalSearchExplorePage/constants';
import { getFilterSlugType } from '../helpers';

export const useGetMetaInfoForInternalSearchExplorePage = () => {
  const router = useRouter();
  const { query, asPath } = router;
  const hasFilterQuery = asPath.includes('?');
  const { keywords, filter } = query;
  const intl = useIntl();

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = intl.formatDate(currentDate, {
    month: 'long',
  });
  const searchKeyword = StringUtils.convertOtherCaseToTitleCase(
    StringUtils.convertQueryToSingleString(keywords ?? '')
  );
  const name = {
    robots: isNotEmpty(filter) ? 'index follow' : 'noindex nofollow',
  };

  const mainKeywordVariable = {
    keyword: searchKeyword,
    currentMonth,
    currentYear,
  };

  if (keywords && !hasFilterQuery && !filter) {
    return {
      title: intl.formatMessage(
        MetaDataLocaleKey.mainKeyword.title,
        mainKeywordVariable
      ),
      description: intl.formatMessage(
        MetaDataLocaleKey.mainKeyword.description,
        mainKeywordVariable
      ),
      name: {
        robots: 'index follow',
      },
    };
  }

  const filterString = StringUtils.convertQueryToSingleString(filter);
  const {
    filterSlug,
    isJobType,
    isEducationLevel,
    isLocationName,
    isWorkArrangementOption,
  } = getFilterSlugType(filterString);

  if (hasFilterQuery || !filterString) return { name };

  const selectedFilterSlug =
    StringUtils.convertOtherCaseToTitleCase(
      filterSlug?.toLowerCase(),
      StringCaseType.SnakeCase
    ) ?? '';

  const variable = {
    ...mainKeywordVariable,
    ...(isEducationLevel &&
      selectedFilterSlug && {
        education: selectedFilterSlug,
      }),
    ...(isWorkArrangementOption && {
      workArrangements: selectedFilterSlug,
    }),
    ...(isJobType && {
      jobType: selectedFilterSlug,
    }),
  };

  if (isJobType) {
    return {
      title: intl.formatMessage(
        MetaDataLocaleKey.jobTypeFilter.title,
        variable
      ),
      description: intl.formatMessage(
        MetaDataLocaleKey.jobTypeFilter.description,
        variable
      ),
      name,
    };
  }

  if (isEducationLevel) {
    return {
      title: intl.formatMessage(
        MetaDataLocaleKey.educationLevelFilter.title,
        variable
      ),
      description: intl.formatMessage(
        MetaDataLocaleKey.educationLevelFilter.description,
        variable
      ),
      name,
    };
  }

  if (isLocationName) {
    const locationName = selectedFilterSlug.substring(3);
    const locationVariable = { ...variable, location: locationName };
    return {
      title: intl.formatMessage(
        MetaDataLocaleKey.locationFilter.title,
        locationVariable
      ),
      description: intl.formatMessage(
        MetaDataLocaleKey.locationFilter.description,
        locationVariable
      ),
      name,
    };
  }

  if (isWorkArrangementOption) {
    return {
      title: intl.formatMessage(
        MetaDataLocaleKey.workArrangementOptionFilter.title,
        variable
      ),
      description: intl.formatMessage(
        MetaDataLocaleKey.workArrangementOptionFilter.description,
        variable
      ),
      name,
    };
  }

  return { name };
};
